import type { ComponentPropsWithoutRef, ReactNode } from 'react';

import { cn } from '@polyseme/utils/cn/cn-utils';
import { cva, type VariantProps } from 'class-variance-authority';

import {
  TooltipArrow,
  TooltipContent,
  Tooltip as TooltipPrimitive,
  TooltipProvider,
  TooltipTrigger,
} from '../../ui/tooltip';
import { Icon } from '../icons';

const SIDE_OPTIONS = ['top', 'right', 'bottom', 'left'] as const;
const ALIGN_OPTIONS = ['start', 'center', 'end'] as const;

type Side = (typeof SIDE_OPTIONS)[number];
type Align = (typeof ALIGN_OPTIONS)[number];

export type TooltipPosition =
  | 'bottom-center'
  | 'bottom-left'
  | 'bottom-right'
  | 'left-bottom'
  | 'left-center'
  | 'left-top'
  | 'right-bottom'
  | 'right-center'
  | 'right-top'
  | 'top-center'
  | 'top-left'
  | 'top-right';

const positionMap = {
  'top-left': { side: 'top', align: 'start' },
  'top-center': { side: 'top', align: 'center' },
  'top-right': { side: 'top', align: 'end' },
  'bottom-left': { side: 'bottom', align: 'start' },
  'bottom-center': { side: 'bottom', align: 'center' },
  'bottom-right': { side: 'bottom', align: 'end' },
  'left-top': { side: 'left', align: 'start' },
  'left-center': { side: 'left', align: 'center' },
  'left-bottom': { side: 'left', align: 'end' },
  'right-top': { side: 'right', align: 'start' },
  'right-center': { side: 'right', align: 'center' },
  'right-bottom': { side: 'right', align: 'end' },
} satisfies Record<
  TooltipPosition,
  {
    side: Side;
    align: Align;
  }
>;

const tooltipContentVariants = cva('max-w-sm', {
  variants: {
    variant: {
      default: 'bg-accent text-foreground [&_svg]:fill-accent',
      inverted: 'bg-foreground text-background [&_svg]:fill-foreground',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export type TooltipProps = Omit<
  ComponentPropsWithoutRef<typeof TooltipContent>,
  'children' | 'content'
> &
  VariantProps<typeof tooltipContentVariants> & {
    className?: string;
    children?: ReactNode;
    content: ReactNode;
    delayDuration?: number;
    position?: TooltipPosition;
  };

export const Tooltip = ({
  children,
  content,
  className,
  delayDuration = 300,
  position = 'top-center',
  variant = 'default',
  ...props
}: TooltipProps) => {
  const { side, align } = positionMap[position];

  return (
    <TooltipProvider>
      <TooltipPrimitive delayDuration={delayDuration}>
        <TooltipTrigger asChild>
          {children ?? (
            <button
              aria-label="More information"
              className={cn('text-primary', className)}
              type="button"
            >
              <Icon className="size-4" name="info" />
            </button>
          )}
        </TooltipTrigger>
        <TooltipContent
          align={align}
          className={tooltipContentVariants({ variant })}
          side={side}
          {...props}
        >
          {content}
          <TooltipArrow />
        </TooltipContent>
      </TooltipPrimitive>
    </TooltipProvider>
  );
};
